import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Loader2, Trash2, ChevronLeft, ChevronRight, AlertTriangle, Image, Video } from 'lucide-react';
import { formatRelativeTime } from './shared';

const KIND_OPTIONS = [
  { id: 'all', label: 'All kinds' },
  { id: 'avatar', label: 'Avatars' },
  { id: 'video', label: 'Videos' },
];

const KindIcon = ({ kind }) => {
  if (kind === 'avatar') return <Image className="w-4 h-4 text-indigo-300" />;
  if (kind === 'video') return <Video className="w-4 h-4 text-purple-300" />;
  return null;
};

export const AdminOrphanedPanel = ({ data, params, onChangeParams, onPurge, purging }) => {
  const items = data?.items || [];
  const total = data?.total || 0;
  const byKind = data?.by_kind || { avatar: 0, video: 0 };
  const offset = params?.offset || 0;
  const limit = params?.limit || 20;
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Card className="bg-zinc-950/60 border-white/5">
      <CardHeader className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-muted-foreground" />
            Orphaned Storage Objects
            <span className="text-sm text-muted-foreground font-normal">({total})</span>
            <Badge variant="outline" className="ml-2 text-indigo-300 border-indigo-500/30 text-[10px]">
              {byKind.avatar} avatars
            </Badge>
            <Badge variant="outline" className="text-purple-300 border-purple-500/30 text-[10px]">
              {byKind.video} videos
            </Badge>
          </CardTitle>
          <Button
            onClick={onPurge}
            disabled={purging || total === 0}
            className="bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 border border-cyan-500/30"
            data-testid="admin-orphans-purge-button"
          >
            {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Run purge now
          </Button>
        </div>
        <div className="flex items-start gap-3 flex-wrap">
          <Select
            value={params?.kind || 'all'}
            onValueChange={(v) => onChangeParams({ ...params, kind: v === 'all' ? undefined : v, offset: 0 })}
          >
            <SelectTrigger className="w-44 bg-zinc-900/60 border-white/10" data-testid="admin-orphans-kind-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-amber-200/80 max-w-xl">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
            <span>
              Emergent storage doesn't expose a delete API yet, so the janitor currently marks objects as
              <code className="mx-1 px-1.5 py-0.5 rounded bg-amber-500/10">pending_delete_api</code>
              and tries again later. Real purging activates automatically once the API supports DELETE.
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            🎉 No orphaned storage objects. The queue is clean.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-[480px] overflow-y-auto" data-testid="admin-orphans-list">
            {items.map((it, idx) => (
              <div
                key={`${it.path}-${idx}`}
                className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/40 border border-white/5"
                data-testid={`admin-orphan-row-${idx}`}
              >
                <KindIcon kind={it.kind} />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs break-all">{it.path}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span>reason: {it.reason}</span>
                    <span>·</span>
                    <span>user: {it.user_id.slice(0, 8)}…</span>
                    {it.last_status && (
                      <>
                        <span>·</span>
                        <span className="text-amber-300">last_status: {it.last_status}</span>
                      </>
                    )}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap" title={new Date(it.orphaned_at).toLocaleString()}>
                  {formatRelativeTime(it.orphaned_at)}
                </span>
              </div>
            ))}
          </div>
        )}
        {total > limit && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-white/5">
            <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={offset === 0}
                onClick={() => onChangeParams({ ...params, offset: Math.max(0, offset - limit) })}
                data-testid="admin-orphans-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data?.has_more}
                onClick={() => onChangeParams({ ...params, offset: offset + limit })}
                data-testid="admin-orphans-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
